"use client";

/**
 * Fires the listing-view tracking beacon once per mount, from every surface
 * where a buyer actually views a listing:
 *
 *   - `app/(site)/listings/[id]/page.tsx` (server component; default
 *     `surface="listing_page"`) — only fires on a real client mount, never
 *     from server-side render/prefetch/bots, which would inflate counts.
 *   - `components/chat/ChatListingDetailSheet.tsx` (`surface="chat_sheet"`)
 *     — mounted inside the sheet's listing content, which both render sites
 *     (chat-message.tsx non-embedded: `if (!isOpen) return null`;
 *     GlobalQuickCheckout.tsx embedded: conditional render on
 *     `sheetOpen && step === "detail"`) unmount while closed, so the tracker
 *     never fires for a closed sheet. Re-opening remounts it — a new view,
 *     by design (no write-time dedup).
 *
 * Deliberately NOT mounted from the shared `ListingDetailPageView` (also
 * used by `app/admin/listings/[id]/page.tsx` for admin/moderation
 * previews) — admin previews must not count as buyer views.
 *
 * `useEffect` here is syncing with the outside world (a tracking beacon),
 * not deriving state from props — allowed per the HARD GATE. The ref
 * guards against React Strict Mode's dev-only double-invoke of effects
 * firing two beacons for one real mount.
 *
 * The guard tracks the LAST `listingId` fired for (not a plain boolean) —
 * Next.js App Router does not force-remount this subtree when navigating
 * between two listing pages under the same `[id]` segment, so the effect
 * re-runs with a new `listingId` on the SAME component instance. A boolean
 * "have I ever fired" guard would silently swallow every view after the
 * first listing visited in a session; comparing against the fired-for id
 * still de-dupes StrictMode's double-invoke (same id, same render) while
 * firing again on a genuine navigation to a different listing.
 *
 * @see lib/analytics/track-listing-view.ts — canonical builder (no inline
 *   fetch/sendBeacon here — Silent NACK)
 */

import { useEffect, useRef } from "react";
import type { ListingViewSurface } from "@kraahl/contract";
import { trackListingView } from "@/lib/analytics/track-listing-view";
import { getGuestId } from "@/lib/guest";

export function ListingViewTracker({
  listingId,
  surface = "listing_page",
}: {
  listingId: string;
  /** Where this tracker lives. Constant per mount site — not part of the ref guard. */
  surface?: ListingViewSurface;
}) {
  const firedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (firedForRef.current === listingId) return;
    firedForRef.current = listingId;
    trackListingView({ listingId, guestId: getGuestId() || null, surface });
  }, [listingId, surface]);

  return null;
}
